import '../page.module.css';

import React, {
	forwardRef,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from 'react';
import { useAppDispatch, useAppSelector } from '../../../redux/hook';

import { getFlow } from '@incodetech/welcome';
import { onboardingState } from '../../../redux/slices/detSlice';
import { setGuestVerificationLazyLoad } from '../../../redux/slices/sessionSlice';

const isIncodeCppNotReadyError = (error: unknown) => {
	if (!error) return false;

	const valuesToCheck = [
		error,
		typeof error === 'object' && error !== null && 'message' in error
			? (error as { message?: unknown }).message
			: '',
		typeof error === 'object' && error !== null && 'name' in error
			? (error as { name?: unknown }).name
			: '',
	]
		.flat()
		.map((value) => String(value).toLowerCase());

	return valuesToCheck.some(
		(value) =>
			value.includes('cpp api') ||
			value.includes("hasn't been initialized") ||
			value.includes('unable to set camera info') ||
			value.includes('not initialized')
	);
};

export interface ChildHandle {
	// initializeSDK: () => Promise<boolean>;
	callIdentityVerification: () => Promise<void>;
}
interface FaceLoginFormProps {
	onSuccess?: (response: any) => void;
	onError?: (error: any) => void;
	incode?: any;
	onSubStepChange?: (subStep: 'intro' | 'camera') => void;
	initializeSDK?: () => Promise<boolean>;
	subStep?: 'intro' | 'camera';
}

const FaceLoginForm = forwardRef<ChildHandle, FaceLoginFormProps>(
	(
		{
			onSuccess,
			onError,
			incode: incodeProp,
			onSubStepChange,
			initializeSDK,
			subStep: subStepProp = 'camera',
		},
		ref
	) => {
		const containerRef = useRef<HTMLDivElement | null>(null);
		const initializedRef = useRef(false);
		const onSuccessRef = useRef(onSuccess);
		const onErrorRef = useRef(onError);
		const { onboardingToken } = useAppSelector(onboardingState);
		const [subStep, setSubStep] = useState<'intro' | 'camera'>(subStepProp);
		const dispatch = useAppDispatch();

		useEffect(() => {
			setSubStep(subStepProp);
		}, [subStepProp]);

		useEffect(() => {
			onSuccessRef.current = onSuccess;
		}, [onSuccess]);

		useEffect(() => {
			onErrorRef.current = onError;
		}, [onError]);

		// useEffect(() => {
		// 	onSubStepChange?.(subStep);
		// }, [subStep, onSubStepChange]);

		const startCamera = async () => {
			if (!incodeProp) {
				console.error('Incode SDK not initialized');
				return;
			}
			// if (subStep === 'intro') {
			// 	setShowTakeSelfieModal(true);
			// 	return;
			// }

			// if (subStep !== 'camera') {
			// 	setSubStep('camera');
			// 	return;
			// }
		};

		useImperativeHandle(ref, () => ({
			callIdentityVerification: startCamera,
		}));

		useEffect(() => {
			if (!incodeProp || !containerRef.current || !onboardingToken) return;

			// 🚫 prevent multiple initializations
			if (initializedRef.current) return;
			initializedRef.current = true;

			let isMounted = true;

			dispatch(setGuestVerificationLazyLoad(false));

			const setupIncode = async () => {
				try {
					if (initializeSDK) {
						const success = await initializeSDK();
						if (!success) {
							dispatch(setGuestVerificationLazyLoad(false));
							return;
						}
					}

					if (!isMounted) return;
					const container = containerRef.current;
					if (!container) return;

					try {
						incodeProp.renderLogin(container, {
							session: { token: onboardingToken },
							stopAtError: true,
							token: onboardingToken,
							onSuccess: (resp: any) => {
								console.log('Face login success:', resp);
								if (!isMounted) return;
								dispatch(setGuestVerificationLazyLoad(false));
								onSuccessRef.current?.(true);
							},
							onError: (err: any) => {
								console.error('Face login error:', err);
								if (!isMounted) return;
								dispatch(setGuestVerificationLazyLoad(false));
								onErrorRef.current?.(err);
							},
						});
					} catch (err) {
						console.error('Face login failed to render auth face:', err);
						if (!isMounted) return;
						dispatch(setGuestVerificationLazyLoad(false));
						onErrorRef.current?.(err);
					}
				} catch (err) {
					console.error('Face login Incode setup error', err);
					if (!isMounted) return;
					dispatch(setGuestVerificationLazyLoad(false));
					onErrorRef.current?.(err);
				}
			};

			// const setupIncode = async () => {
			// 	try {
			// 		if (initializeSDK) {
			// 			const success = await initializeSDK();
			// 			if (!success) {
			// 				dispatch(setGuestVerificationLazyLoad(false));
			// 				return;
			// 			}
			// 		}

			// 		if (!isMounted) return;

			// 		const flow = await getFlow(onboardingToken);
			// 		if (!isMounted) return;

			// 		const authenticationModule = flow.flowModules?.find(
			// 			(module) => module.key === 'AUTHENTICATION'
			// 		);
			// 		if (!authenticationModule?.configuration) {
			// 			const error = {
			// 				code: 'INCODE_AUTHENTICATION_NOT_CONFIGURED',
			// 				message:
			// 					'The Incode session workflow is missing its authentication configuration.',
			// 			};
			// 			console.error('Face login workflow error:', error);
			// 			dispatch(setGuestVerificationLazyLoad(false));
			// 			onErrorRef.current?.(error);
			// 			return;
			// 		}

			// 		// Retry renderLogin with exponential back-off to handle the
			// 		// "cpp API hasn't been initialized" race condition on slow/iOS devices.
			// 		const MAX_RETRIES = 5;
			// 		const BASE_DELAY_MS = 500;

			// 		try {
			// 			incodeProp.renderAuthFace(containerRef.current, {
			// 				session: { token: onboardingToken },
			// 				authHint: email,
			// 				onSuccess: (resp: any) => {
			// 					console.log('Face login success:', resp);
			// 				},

			// 				onError: (err: any) => {
			// 					console.error('Face login error:', err);
			// 				},
			// 			});
			// 		} catch (err) {
			// 			console.error('Face login failed to render auth face:', err);
			// 		}

			// 		// for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
			// 		// 	if (!isMounted) return;

			// 		// 	// Wait before each attempt (500 → 1000 → 2000 → 4000 → 8000 ms)
			// 		// 	await new Promise<void>((resolve) =>
			// 		// 		setTimeout(resolve, BASE_DELAY_MS * Math.pow(2, attempt))
			// 		// 	);

			// 		// 	if (!isMounted) return;

			// 		// 	try {
			// 		// 		incodeProp.renderAuthFace(containerRef.current, {
			// 		// 			session: { token: onboardingToken },
			// 		// 			authHint: email,
			// 		// 			onSuccess: (resp: any) => {
			// 		// 				console.log('Face login success:', resp);
			// 		// 				if (!isMounted) return;
			// 		// 				dispatch(setGuestVerificationLazyLoad(false));
			// 		// 				onSuccessRef.current?.(true);
			// 		// 			},

			// 		// 			onError: (err: any) => {
			// 		// 				console.error('Face login error:', err);
			// 		// 				if (!isMounted) return;
			// 		// 				dispatch(setGuestVerificationLazyLoad(false));
			// 		// 				onErrorRef.current?.(err);
			// 		// 				// if (!isMounted) return;
			// 		// 				// dispatch(setGuestVerificationLazyLoad(false));
			// 		// 				// // allow React state to settle
			// 		// 				// setTimeout(() => {
			// 		// 				// 	if (isIncodeCppNotReadyError(err)) {
			// 		// 				// 		onErrorRef.current?.({
			// 		// 				// 			code: 'INCODE_CPP_NOT_READY',
			// 		// 				// 			message:
			// 		// 				// 				'Unable to initialize camera. Please try again.',
			// 		// 				// 			error: err,
			// 		// 				// 		});
			// 		// 				// 		return;
			// 		// 				// 	}

			// 		// 				// 	onErrorRef.current?.(err);
			// 		// 				// }, 0);
			// 		// 			},
			// 		// 		});

			// 		// 		// incodeProp.renderAuthFace(containerRef.current, {
			// 		// 		// 	stopAtError: true,
			// 		// 		// 	assistedOnboarding: false,
			// 		// 		// 	hatCheckEnabled: false,
			// 		// 		// 	lensesCheckEnabled: true,
			// 		// 		// 	maskCheckEnabled: true,
			// 		// 		// 	eyesClosedCheckEnabled: true,
			// 		// 		// 	token: onboardingToken,
			// 		// 		// 	numberOfTries: 3,
			// 		// 		// 	showCustomCameraPermissionScreen: false,
			// 		// 		// 	timeout: 30000,

			// 		// 		// 	onSuccess: (resp: any) => {
			// 		// 		// 		if (!isMounted) return;
			// 		// 		// 		dispatch(setGuestVerificationLazyLoad(false));
			// 		// 		// 		onSuccessRef.current?.(true);
			// 		// 		// 	},

			// 		// 		// 	onError: (err: any) => {
			// 		// 		// 		if (!isMounted) return;
			// 		// 		// 		dispatch(setGuestVerificationLazyLoad(false));
			// 		// 		// 		// allow React state to settle
			// 		// 		// 		setTimeout(() => {
			// 		// 		// 			if (isIncodeCppNotReadyError(err)) {
			// 		// 		// 				onErrorRef.current?.({
			// 		// 		// 					code: 'INCODE_CPP_NOT_READY',
			// 		// 		// 					message:
			// 		// 		// 						'Unable to initialize camera. Please try again.',
			// 		// 		// 					error: err,
			// 		// 		// 				});
			// 		// 		// 				return;
			// 		// 		// 			}

			// 		// 		// 			onErrorRef.current?.(err);
			// 		// 		// 		}, 0);
			// 		// 		// 	},
			// 		// 		// });

			// 		// 		// renderLogin called successfully — stop retrying
			// 		// 		return;
			// 		// 	} catch (e: any) {
			// 		// 		const msg: string = (e?.message ?? '').toLowerCase();
			// 		// 		const isCppNotReady = isIncodeCppNotReadyError(e);

			// 		// 		if (isCppNotReady && attempt < MAX_RETRIES - 1) {
			// 		// 			console.warn(
			// 		// 				`Incode cpp API not ready, retrying (${
			// 		// 					attempt + 1
			// 		// 				}/${MAX_RETRIES})…`,
			// 		// 				e
			// 		// 			);
			// 		// 			continue;
			// 		// 		}

			// 		// 		// Unexpected error or retries exhausted
			// 		// 		console.error('Incode render error', e);
			// 		// 		dispatch(setGuestVerificationLazyLoad(false));
			// 		// 		if (isCppNotReady) {
			// 		// 			onErrorRef.current?.({
			// 		// 				message: 'Camera initialization failed. Please try again.',
			// 		// 			});
			// 		// 		}
			// 		// 		return;
			// 		// 	}
			// 		// }
			// 	} catch (err) {
			// 		console.error('Face login Incode setup error', err);
			// 		if (!isMounted) return;
			// 		dispatch(setGuestVerificationLazyLoad(false));
			// 		onErrorRef.current?.(err);
			// 	}
			// };

			// setupIncode();
			setupIncode();

			return () => {
				isMounted = false;
				initializedRef.current = false;

				// ✅ only SDK cleanup (DO NOT touch DOM manually)
				try {
					incodeProp?.clean?.();
				} catch (e) {
					console.log('Safe SDK cleanup', e);
				}
			};
		}, [incodeProp, onboardingToken]);
		// useEffect(() => {
		// 	if (!incodeProp || !containerRef.current || !onboardingToken) return;

		// 	if (initializedRef.current) return;
		// 	initializedRef.current = true;

		// 	let isMounted = true;

		// 	// store timers so we can clear them
		// 	const timers: NodeJS.Timeout[] = [];

		// 	dispatch(setGuestVerificationLazyLoad(true));

		// 	const setupIncode = async () => {
		// 		try {
		// 			if (initializeSDK) {
		// 				const success = await initializeSDK();
		// 				if (!success) return;
		// 			}

		// 			if (!isMounted) return;

		// 			// slight delay (iOS / DOM readiness)
		// 			const renderTimer = setTimeout(() => {
		// 				try {
		// 					incodeProp.renderLogin(containerRef.current, {
		// 						stopAtError: true,
		// 						assistedOnboarding: false,
		// 						hatCheckEnabled: false,
		// 						lensesCheckEnabled: true,
		// 						maskCheckEnabled: true,
		// 						eyesClosedCheckEnabled: true,
		// 						token: onboardingToken,
		// 						numberOfTries: 3,
		// 						showCustomCameraPermissionScreen: true,
		// 						timeout: 30000,

		// 						onSuccess: (resp: any) => {
		// 							if (!isMounted) return;
		// 							dispatch(setGuestVerificationLazyLoad(false));
		// 							onSuccessRef.current?.(true);
		// 						},

		// 						onError: (err: any) => {
		// 							if (!isMounted) return;
		// 							// let React settle
		// 							setTimeout(() => {
		// 								onErrorRef.current?.(err);
		// 							}, 0);
		// 							dispatch(setGuestVerificationLazyLoad(false));
		// 						},
		// 					});

		// 					// fallback loader OFF (safety)
		// 					const loaderTimer = setTimeout(() => {
		// 						if (isMounted) {
		// 							dispatch(setGuestVerificationLazyLoad(false));
		// 						}
		// 					}, 2000);

		// 					timers.push(loaderTimer);
		// 				} catch (e) {
		// 					console.error('Incode render error', e);
		// 					dispatch(setGuestVerificationLazyLoad(false));
		// 				}
		// 			}, 100);

		// 			timers.push(renderTimer);
		// 		} catch (err) {
		// 			console.error('Incode setup error', err);
		// 			dispatch(setGuestVerificationLazyLoad(false));
		// 		}
		// 	};

		// 	setupIncode();

		// 	return () => {
		// 		isMounted = false;
		// 		initializedRef.current = false;

		// 		// ✅ clear all timers
		// 		timers.forEach(clearTimeout);

		// 		// ✅ SDK cleanup only
		// 		try {
		// 			incodeProp?.clean?.();
		// 		} catch (e) {
		// 			console.log('Safe SDK cleanup', e);
		// 		}
		// 	};
		// }, [incodeProp, onboardingToken]);
		// useEffect(() => {
		// 	if (!incodeProp || !containerRef.current || !onboardingToken) return;

		// 	// 🚫 prevent multiple initializations
		// 	if (initializedRef.current) return;
		// 	initializedRef.current = true;

		// 	let isMounted = true;

		// 	dispatch(setGuestVerificationLazyLoad(true));

		// 	const setupIncode = async () => {
		// 		try {
		// 			if (initializeSDK) {
		// 				const success = await initializeSDK();
		// 				if (!success) {

		// 					return;
		// 				}
		// 			}

		// 			if (!isMounted) return;

		// 			setTimeout(() => {
		// 						dispatch(setGuestVerificationLazyLoad(false));
		// 					}, 3000);
		// 			// small delay helps avoid race conditions (especially iOS)
		// 			setTimeout(() => {
		// 				try {

		// 					incodeProp.renderLogin(containerRef.current, {
		// 						stopAtError: true,
		// 						assistedOnboarding: false,
		// 						hatCheckEnabled: false,
		// 						lensesCheckEnabled: true,
		// 						maskCheckEnabled: true,
		// 						eyesClosedCheckEnabled: true,
		// 						token: onboardingToken,
		// 						numberOfTries: 3,
		// 						showCustomCameraPermissionScreen: true,
		// 						timeout: 30000,

		// 						onSuccess: (resp: any) => {
		// 							if (!isMounted) return;
		// 							dispatch(setGuestVerificationLazyLoad(false));
		// 							onSuccessRef.current?.(true);
		// 						},

		// 						onError: (err: any) => {
		// 							if (!isMounted) return;

		// 							dispatch(setGuestVerificationLazyLoad(false));
		// 							setShowTakeSelfieModal(false);

		// 							// allow React state to settle
		// 							setTimeout(() => {
		// 								onErrorRef.current?.(err);
		// 							}, 0);
		// 						},
		// 					});
		// 					// Hide loading once renderLogin is called
		// 					setTimeout(() => {
		// 						if (isMounted) {
		// 							dispatch(setGuestVerificationLazyLoad(false));
		// 						}
		// 					}, 1500);
		// 				} catch (e) {
		// 					console.error('Incode render error', e);
		// 					dispatch(setGuestVerificationLazyLoad(false));
		// 				}
		// 			}, 300);
		// 		} catch (err) {
		// 			console.error('Incode setup error', err);
		// 		}
		// 	};

		// 	setupIncode();

		// 	return () => {
		// 		isMounted = false;
		// 		initializedRef.current = false;

		// 		// ✅ only SDK cleanup (DO NOT touch DOM manually)
		// 		try {
		// 			incodeProp?.clean?.();
		// 		} catch (e) {
		// 			console.log('Safe SDK cleanup', e);
		// 		}
		// 	};
		// }, [incodeProp, onboardingToken]);

		// useEffect(() => {
		// 	// if (subStep !== 'camera') return;
		// 	if (!incodeProp) return;

		// 	setLoading(true);
		// 	if (!containerRef.current) {
		// 		console.error('Camera container not ready');
		// 		setLoading(false);
		// 		return;
		// 	}

		// 	incodeProp.renderLogin(containerRef.current, {
		// 		stopAtError: true,
		// 		assistedOnboarding: false,
		// 		hatCheckEnabled: false,
		// 		lensesCheckEnabled: true,
		// 		maskCheckEnabled: true,
		// 		eyesClosedCheckEnabled: true,
		// 		token: onboardingToken, // Don't forget to pass the token!
		// 		numberOfTries: 3,
		// 		showCustomCameraPermissionScreen: true,
		// 		timeout: 30000,
		// 		onSuccess: (resp: any) => {
		// 			setLoading(false);
		// 			onSuccessRef.current?.(true);
		// 		},
		// 		onError: (err: any) => {
		// 			setLoading(false);
		// 			setShowTakeSelfieModal(false);
		// 			// setSubStep('intro');

		// 			window.setTimeout(() => {
		// 				onErrorRef.current?.(err);
		// 			}, 0);
		// 		},
		// 	});
		// 	// const isDev = true;
		// 	// if (isDev) {
		// 	// 	console.log('Mock Incode Login Triggered');

		// 	// 	setTimeout(() => {
		// 	// 		setLoading(false);
		// 	// 		onSuccess?.({
		// 	// 			interviewId: 'mock-session-001',
		// 	// 			status: 'completed',
		// 	// 			result: 'approved',
		// 	// 			success: true,
		// 	// 		});
		// 	// 	}, 1500); // simulate processing delay

		// 	// 	return; // VERY IMPORTANT
		// 	// }

		// 	// const timer = setTimeout(() => {

		// 	// }, 0);

		// 	// 🔥 IMPORTANT CLEANUP
		// 	return () => {
		// 		// clearTimeout(timer);

		// 		// 🛡️ Stop all active camera tracks so iOS re-prompts on next mount
		// 		try {
		// 			incodeProp?.clean?.();
		// 		} catch (e) {
		// 			console.log('Safe SDK cleanup', e);
		// 		}

		// 		// Stop every video track inside the container
		// 		const container = containerRef.current;
		// 		if (container) {
		// 			container
		// 				.querySelectorAll('video')
		// 				.forEach((videoEl: HTMLVideoElement) => {
		// 					const stream = videoEl.srcObject;
		// 					if (stream instanceof MediaStream) {
		// 						stream.getTracks().forEach((track) => track.stop());
		// 					}
		// 					videoEl.srcObject = null;
		// 				});
		// 			container.innerHTML = '';
		// 		}

		// 		// Also scan the whole document for any orphaned video streams (iOS safety)
		// 		document
		// 			.querySelectorAll('video')
		// 			.forEach((videoEl: HTMLVideoElement) => {
		// 				const stream = videoEl.srcObject;
		// 				if (stream instanceof MediaStream) {
		// 					stream.getTracks().forEach((track) => track.stop());
		// 					videoEl.srcObject = null;
		// 				}
		// 			});
		// 	};
		// }, [incodeProp]);
		const isCameraLoading = useAppSelector(
			(state) => state.session.guestVerificationLazyLoad
		);
		return (
			<div className="selfie-wrapper d-flex flex-column align-items-center justify-content-center w-100">
				{/* INTRO SCREEN */}
				{subStep === 'intro' && (
					<div
						className="intro-card d-flex flex-column 
						align-items-center text-center"
						role="button">
						<img
							src={process.env.PUBLIC_URL + '/take-selfie.png'}
							alt="Take Selfie"
							style={{ height: '250px' }}
							className="img-fluid"
						/>

						<h2 className="mb-2 fw-bold">Get Ready for Your Selfie</h2>

						<small className="text-muted">
							We’re about to take a selfie to verify your identity. Please keep
							a neutral facial expression and make sure your face is clearly
							visible.
						</small>
					</div>
				)}

				{/* CAMERA SCREEN */}
				{subStep === 'camera' && (
					<div className="camera-container d-flex flex-column align-items-center w-100">
						<div className="camera-frame w-100 d-flex justify-content-center">
							<div
								ref={containerRef}
								className="incode-camera-host d-flex align-items-center justify-content-center position-relative">
								{/* Loading handled by DisplayPage */}
								{isCameraLoading && (
									<div className="camera-overlay-loader">
										<div className="spinner-border text-light" />
										<div className="mt-2 text-light small">
											Opening camera...
										</div>
									</div>
								)}
							</div>
						</div>

						<div className="text-center mt-3 small fw-semibold text-dark">
							{/* Align your face in the center of the frame */}
						</div>
					</div>
				)}

				{/* <TakeSelfieModal
					show={showTakeSelfieModal}
					onClose={() => setShowTakeSelfieModal(false)}
					onUnderstand={() => {
						setShowTakeSelfieModal(false);
						setSubStep('camera');
					}}
				/> */}
			</div>
		);
	}
);
FaceLoginForm.displayName = 'FaceLoginForm';
export default FaceLoginForm;
